import type { ReactNode } from 'react';
import type { LinhaCrianca } from '../types';
import {
  digitosTelefone,
  idadeTexto,
  normalizar,
  ouTraco,
  temAlergia,
  temRestricao,
  termoAssinado,
  tomaRemedio,
} from '../utils';

interface Props {
  linha: LinhaCrianca;
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[12px] font-medium text-foreground-500">{rotulo}</p>
      <div className="mt-0.5 min-w-0 break-words text-[14px] leading-relaxed text-foreground-900 [overflow-wrap:anywhere]">
        {children}
      </div>
    </div>
  );
}

// Um cartão por criança: o que a equipe das crianças precisa saber no papel.
export default function CartaoCrianca({ linha }: Props) {
  const assinado = termoAssinado(linha);
  const alergia = temAlergia(linha);
  const remedio = tomaRemedio(linha);
  const restricao = temRestricao(linha);
  const idade = idadeTexto(linha);
  const nascimento = normalizar(linha.nascimento);

  const digitos = digitosTelefone(linha.telefone);
  const zap =
    digitos.length >= 10
      ? `https://wa.me/${digitos.startsWith('55') ? digitos : `55${digitos}`}`
      : null;

  return (
    <article className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-background-200 bg-white p-4">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-[16px] font-bold leading-snug text-foreground-950 [overflow-wrap:anywhere]">
            {normalizar(linha.crianca) || 'Criança sem nome'}
          </h3>
          <p className="mt-0.5 break-words text-[13px] text-foreground-600">
            {idade}
            {nascimento ? ` · ${nascimento}` : ''}
          </p>
        </div>
        <span
          className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold ${
            assinado ? 'bg-success-50 text-success-700' : 'bg-warning-50 text-warning-800'
          }`}
        >
          {assinado ? 'Termo assinado' : 'Falta assinar'}
        </span>
      </div>

      <div className="mt-2 flex min-w-0 items-center gap-1.5">
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
          <i className="ri-user-3-line" aria-hidden />
        </span>
        <p className="min-w-0 flex-1 truncate text-[13px] text-foreground-700">
          {normalizar(linha.responsavel) || 'Responsável sem nome'}
        </p>
        {zap && (
          <a
            href={zap}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`WhatsApp de ${normalizar(linha.responsavel) || 'responsável'}`}
            className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-success-600 transition-colors hover:bg-success-50"
          >
            <i className="ri-whatsapp-line text-xl" aria-hidden />
          </a>
        )}
      </div>

      <div className="mt-2 min-w-0 space-y-2.5 border-t border-background-200 pt-3">
        <Linha rotulo="Alergias">
          {alergia ? (
            <span className="inline-block max-w-full whitespace-pre-line rounded-md bg-danger-50 px-2 py-0.5 font-semibold text-danger-700 [overflow-wrap:anywhere]">
              {normalizar(linha.alergias)}
            </span>
          ) : (
            <span className="whitespace-pre-line">{ouTraco(linha.alergias)}</span>
          )}
        </Linha>

        <Linha rotulo="Alimentação">
          {restricao ? (
            <span className="whitespace-pre-line font-medium text-foreground-950">
              {normalizar(linha.alimentacao)}
            </span>
          ) : (
            <span className="whitespace-pre-line">{ouTraco(linha.alimentacao)}</span>
          )}
        </Linha>

        <Linha rotulo="Remédios e horários">
          {remedio ? (
            <span className="inline-block max-w-full whitespace-pre-line rounded-md bg-warning-50 px-2 py-1 font-medium text-warning-900 [overflow-wrap:anywhere]">
              {normalizar(linha.remedios)}
            </span>
          ) : (
            <span className="whitespace-pre-line">{ouTraco(linha.remedios)}</span>
          )}
        </Linha>

        <Linha rotulo="Febre ou dor">
          <span className="whitespace-pre-line">{ouTraco(linha.febre_dor)}</span>
        </Linha>

        <Linha rotulo="Saúde e cuidados">
          <span className="whitespace-pre-line">{ouTraco(linha.saude)}</span>
        </Linha>

        <Linha rotulo="Contato de emergência">
          <span className="whitespace-pre-line">{ouTraco(linha.contato_emergencia)}</span>
        </Linha>
      </div>

      <p className="mt-3 min-w-0 break-words border-t border-background-200 pt-2 text-[12px] text-foreground-500 [overflow-wrap:anywhere]">
        {normalizar(linha.termo) || 'Termo ainda não assinado.'}
      </p>
    </article>
  );
}